export const trustLogos = [
  "Hartley Construct",
  "Marlow Build Group",
  "Pendle & Co.",
  "Northgate Civils",
  "Brookfield Trades",
  "Ashridge Roofing",
  "Caldwell Joinery",
  "Stoneham Plumbing",
];

export const accreditations = [
  "CHAS",
  "SafeContractor",
  "ISO 9001",
  "Constructionline",
  "Google Partner",
];

export type WhyPoint = { kicker: string; title: string; body: string; alt: string };

// NOTE: `title` doubles as the illustration filename in /public, so keep titles unchanged.
export const whyPoints: WhyPoint[] = [
  {
    kicker: "Industry only",
    title: "We only work with construction.",
    body: "ConstructionWebDesign.uk only builds websites for construction companies and trades. No restaurants, no shops. That focus means the pages a builder, roofer or electrician needs are planned before your discovery call even starts.",
    alt: "Illustration of a builder in a hard hat fitting a section onto a website layout, with a trowel and brick beside him",
  },
  {
    kicker: "Built to sell",
    title: "Built to bring in work, not awards.",
    body: "A construction website earns its keep when the phone rings. Every site ships with on-page local SEO, mobile-first pages and a clear call to action on every screen, so visitors know how to ask you for a quote.",
    alt: "Illustration of a tradesperson placing a project photo on a website, with search, message and phone call icons alongside",
  },
  {
    kicker: "No nonsense",
    title: "Straight-talking, fixed pricing.",
    body: "A construction website costs £1,000, one-off, or £1,399 with logo and branding. No subscription, no hourly billing. You see the price before anything starts, and you own the website when it goes live.",
    alt: "Illustration of a builder holding a fixed price sheet next to a website, with a fixed price tag and a signed document",
  },
];

export const homeIncluded: string[] = [
  "Custom domain and hosting setup",
  "Service pages built around the work you want more of",
  "Mobile-first design that works on a phone between jobs",
  "On-page local SEO for your town or city",
  "Project photo galleries and accreditation badges you hold",
  "Click-to-call buttons and a quote request form",
  "Live in 7 days from your discovery call",
  "You own the website, with no monthly fees",
];

export type Service = {
  id: string;
  name: string;
  blurb: string;
  cta: string;
  href?: string;
};

export const services: Service[] = [
  {
    id: "construction-website-design",
    name: "Construction Website Design",
    blurb:
      "Custom-built websites for construction companies that look the part and actually bring in leads. Mobile-first, fast, and easy to update yourself.",
    cta: "See examples",
    href: "/construction-web-design",
  },
  {
    id: "construction-website-development",
    name: "Construction Website Development",
    blurb:
      "Custom-coded web development for construction companies. Fast, clean builds that rank on Google and convert visitors into enquiries — no templates, no platform fees.",
    cta: "See our work",
    href: "/construction-website-development",
  },
  {
    id: "seo-for-construction-companies",
    name: "SEO for Construction Companies",
    blurb:
      "Get found when local customers search for what you do: Google Business Profile, local pages, reviews and technical fixes for UK construction firms.",
    cta: "Get an SEO audit",
    href: "/seo-for-construction-companies",
  },
  {
    id: "construction-lead-generation",
    name: "Construction Lead Generation",
    blurb:
      "Forms, call tracking, and landing pages built to turn website visitors into proper enquiries. Stop letting traffic slip away.",
    cta: "Talk to us",
  },
];

export type Industry = { name: string; subline: string; slug: string };

export const industries: Industry[] = [
  { name: "Main Contractors", subline: "Website design for main contractors", slug: "main-contractors" },
  { name: "Building Firms", subline: "Website design for building firms", slug: "building-firms" },
  { name: "Construction Recruitment", subline: "Website design for construction recruiters", slug: "construction-recruitment" },
  { name: "Product Manufacturers", subline: "Website design for product manufacturers", slug: "product-manufacturers" },
  { name: "Tradesmen", subline: "Website design for tradesmen", slug: "tradesmen" },
  { name: "Plumbers", subline: "Website design for plumbers", slug: "plumbers" },
  { name: "Electricians", subline: "Website design for electricians", slug: "electricians" },
  { name: "Carpenters & Joiners", subline: "Website design for carpenters & joiners", slug: "carpenters" },
  { name: "Roofers", subline: "Website design for roofers", slug: "roofers" },
  { name: "Builders", subline: "Website design for builders", slug: "builders" },
  { name: "Landscapers", subline: "Website design for landscapers", slug: "landscapers" },
  { name: "Architects", subline: "Website design for architects", slug: "architects" },
  { name: "Painters & Decorators", subline: "Website design for painters & decorators", slug: "painters-decorators" },
  { name: "HVAC & Heating", subline: "Website design for HVAC firms", slug: "hvac" },
  { name: "Property Maintenance", subline: "Website design for property maintenance firms", slug: "property-maintenance" },
  { name: "Handyman Services", subline: "Website design for handyman services", slug: "handyman" },
  { name: "Home Improvement", subline: "Website design for home improvement firms", slug: "home-improvement" },
  { name: "Flooring Contractors", subline: "Website design for flooring contractors", slug: "flooring" },
];

export type ProcessStep = { n: string; title: string; body: string };

export const processSteps: ProcessStep[] = [
  {
    n: "01",
    title: "Discovery call (free)",
    body: "A 30-minute call. We learn what you do, where you work and which jobs you want more of.",
  },
  {
    n: "02",
    title: "Strategy & sitemap",
    body: "We plan every page, the search terms each one targets and the enquiry form. You sign it off before design starts.",
  },
  {
    n: "03",
    title: "Design",
    body: "A custom design built around your trade and your photos. You see the draft, give feedback and we adjust it.",
  },
  {
    n: "04",
    title: "Build",
    body: "We build the pages, set up on-page SEO, connect your domain and test the forms and click-to-call buttons.",
  },
  {
    n: "05",
    title: "Launch & support",
    body: "The website goes live on day 7. You get the login details, and we stay on hand for questions after launch.",
  },
];

export type Tier = {
  name: string;
  price: string;
  pitch: string;
  features: string[];
  intro?: string;
  best?: boolean;
  checkoutUrl?: string;
  checkoutLabel?: string;
};

export const tiers: Tier[] = [
  {
    name: "Website",
    price: "£1,000",
    pitch: "A professional, mobile-first site built around your service and ready to bring in jobs.",
    features: [
      "Custom domain + hosting setup",
      "Pages built around your service",
      "Mobile-first design",
      "On-page SEO targeting your area",
      "Live in 7 days",
    ],
    checkoutUrl: "https://buy.stripe.com/6oUbJ1dy4bcCdgB9xqcjS00",
    checkoutLabel: "Get my website",
  },
  {
    name: "Website + Brand",
    price: "£1,399",
    pitch: "Everything you need to launch and look the part — site plus a full brand identity.",
    intro: "Everything in Website, plus:",
    features: [
      "Custom logo design",
      "Full visual identity (colours + typography)",
      "Brand style direction",
      "Business card + document mockups",
    ],
    best: true,
    checkoutUrl: "https://buy.stripe.com/28E4gzfGcbcC90laBucjS01",
    checkoutLabel: "Get website + brand",
  },
];

export type ComparisonRow = {
  option: string;
  price: string;
  time: string;
  catch: string;
  us?: boolean;
};

export const comparison: ComparisonRow[] = [
  {
    option: "ConstructionWebDesign.uk",
    price: "£1,000 fixed · one-off",
    time: "Live in 7 days",
    catch: "You own the website. No subscription and no monthly fees.",
    us: true,
  },
  {
    option: "UK freelancer",
    price: "About £4,000–£5,000",
    time: "Weeks to months",
    catch: "Typically billed at £50–£120 an hour, so the final bill depends on how many hours the job takes.",
  },
  {
    option: "UK agency",
    price: "£5,000–£25,000 a project",
    time: "8–12 weeks",
    catch: "Typical UK range. Longer timelines, and the price usually grows with the scope.",
  },
  {
    option: "Wix / Squarespace",
    price: "About £22 a month",
    time: "Weeks of DIY",
    catch: "The monthly fee adds up over the years, and the site goes offline if you stop paying. You build it yourself or pay someone to.",
  },
];

export type Faq = { q: string; a: string };

export const faqs: Faq[] = [
  {
    q: "How much does a construction website cost?",
    a: "A construction website from ConstructionWebDesign.uk costs £1,000, a one-off fixed price with no subscription. The Website + Brand package costs £1,399 and adds a logo, visual identity and business card mockups. You see the price before any work starts, and there are no monthly fees.",
  },
  {
    q: "How much do other UK web designers charge?",
    a: "A typical UK freelancer charges £50–£120 an hour, which usually comes to about £4,000–£5,000 for a construction website. A typical UK agency quotes £5,000–£25,000 and takes 8–12 weeks. ConstructionWebDesign.uk charges a fixed £1,000 and has the website live in 7 days.",
  },
  {
    q: "Is the website built on WordPress or Webflow?",
    a: "No. ConstructionWebDesign.uk builds every construction website from its own tested templates, so there are no plugins to update and no platform fees to pay each month. The result is a fast, lean website that will not break when a third-party plugin changes.",
  },
  {
    q: "Do I own the website after it's built?",
    a: "Yes. The client owns the website, including the design, the content and the domain. There is no licence fee and no lock-in, so if you ever want to move the website to another host or developer, you can take it with you.",
  },
  {
    q: "How long does a construction website take to build?",
    a: "A construction website from ConstructionWebDesign.uk goes live 7 days after the discovery call. The five steps are a discovery call, strategy and sitemap, design, build, then launch and support. Copy, design and build run side by side, which is how a week is enough.",
  },
  {
    q: "Will my website have one page or several?",
    a: "That depends on the work you want to win. A sole trader often does well with a focused site covering services, projects, reviews and contact. A building firm with several services usually needs a page per service and per area, so each one can rank on Google. The sitemap is agreed on the discovery call.",
  },
  {
    q: "Can I just use a free construction website template?",
    a: "You can, but a free construction website template still needs your content, photos, hosting, domain and local SEO setting up. Most builders end up paying someone for that work. A fixed £1,000 build covers all of it, is designed around your trade, and you own the finished website.",
  },
  {
    q: "Why does a construction company need a website?",
    a: "A construction company needs a website because customers check you online before they call. A website shows your past projects, your accreditations and your reviews, and it takes enquiries while you are on site. Without one, a homeowner or buyer has nothing to compare you on.",
  },
  {
    q: "What should a construction company website include?",
    a: "A construction company website should include photos of real projects, a page for each main service, the accreditations you hold (such as CHAS, FMB, NICEIC or Gas Safe), customer reviews, the areas you cover, a click-to-call button and a short quote form. It should also load quickly on a phone.",
  },
];

export type Demo = {
  slug: string;
  name: string;
  category: string;
  tagline: string;
  href: string;
  isNew?: boolean;
  thumbnail?: string;
  /**
   * Unlisted (client) demo — reachable only by its direct URL. Filtered out of every
   * listing surface (homepage grid, nav dropdown, industry pages) and kept out of the
   * sitemap. Its index.html also carries a `noindex, nofollow` meta so search engines
   * skip it. Flip this off to make the demo public again.
   */
  unlisted?: boolean;
};

export const demos: Demo[] = [
  {
    slug: "avelon",
    name: "Avelon",
    category: "Construction & Architecture",
    tagline: "For architecture studios and premium design-build firms.",
    href: "/demos/avelon/index.html",
  },
  {
    slug: "brassline",
    name: "Brassline",
    category: "Plumbers",
    tagline: "For plumbing and heating firms — emergencies, bathrooms, boilers.",
    href: "/demos/brassline/index.html",
  },
  {
    slug: "brentor",
    name: "Brentor",
    category: "General Contractor",
    tagline: "For commercial contractors and main build firms.",
    href: "/demos/brentor/index.html",
  },
  {
    slug: "constructum",
    name: "Constructum",
    category: "General Construction",
    tagline: "For family-run builders serving residential clients.",
    href: "/demos/constructum/index.html",
  },
  {
    slug: "coreframe",
    name: "Coreframe",
    category: "Construction Firm",
    tagline: "For commercial construction and development firms.",
    href: "/demos/coreframe/index.html",
  },
  {
    slug: "halden",
    name: "Halden & Co.",
    category: "Heritage & Commercial Build",
    tagline: "For heritage, restoration and bespoke residential builders.",
    href: "/demos/halden/index.html",
  },
  {
    slug: "hedgerow",
    name: "Hedgerow",
    category: "Landscapers",
    tagline: "For landscape studios, garden designers and grounds-maintenance firms.",
    href: "/demos/hedgerow/index.html",
    isNew: true,
  },
  {
    slug: "joinwright",
    name: "Joinwright",
    category: "Carpenters & Joiners",
    tagline: "For bespoke carpenters & joiners — kitchens, wardrobes, staircases, doors.",
    href: "/demos/joinwright/index.html",
    isNew: true,
  },
  {
    slug: "skylofts",
    name: "SkyLofts",
    category: "Loft & Garage Conversions",
    tagline: "For loft & garage conversion specialists — dormers, extensions, carpentry.",
    href: "/demos/skylofts/index.html",
    isNew: true,
    unlisted: true,
  },
  {
    slug: "northcrest",
    name: "Northcrest",
    category: "Construction & Building",
    tagline: "For residential and small-commercial construction companies.",
    href: "/demos/northcrest/index.html",
  },
  {
    slug: "renovia",
    name: "Renovia",
    category: "Renovation & Refurbishment",
    tagline: "For renovation and refurbishment companies.",
    href: "/demos/renovia/index.html",
  },
  {
    slug: "ridgemark",
    name: "Ridgemark",
    category: "Roofers",
    tagline: "For roofing contractors — slate, tile, lead, copper, zinc and heritage.",
    href: "/demos/ridgemark/index.html",
    isNew: true,
  },
  {
    slug: "solidcraft",
    name: "Solidcraft Brickwork",
    category: "Bricklayers & Masonry",
    tagline: "For bricklayers, masons and brick extension specialists.",
    href: "/demos/solidcraft/index.html",
  },
  {
    slug: "stackline",
    name: "Stackline",
    category: "Construction Firm",
    tagline: "For premium contractors and architectural-build firms — light, methodical, brochure-style.",
    href: "/demos/stackline/index.html",
    isNew: true,
  },
  {
    slug: "trunkline",
    name: "Trunkline",
    category: "Pre-Construction Plumbing",
    tagline: "For new-build plumbing contractors — wells, mains, trunk and rough-in before the walls go up.",
    href: "/demos/trunkline/index.html",
    isNew: true,
  },
  {
    slug: "voltline",
    name: "Voltline",
    category: "Electricians",
    tagline: "For NICEIC electricians, EV installers and smart-home specialists.",
    href: "/demos/voltline/index.html",
    isNew: true,
  },
  {
    slug: "falkirkhomes",
    name: "Falkirk Homes",
    category: "Estate Agency",
    tagline: "Client redesign — fixed-fee estate agency, no upfront costs, no commission.",
    href: "/demos/falkirkhomes/index.html",
    unlisted: true,
  },
];

import type { Slide } from "@/lib/slides";

export type IndustryPage = {
  slug: string;
  /** 40–60 chars, Ahrefs primary keyword first. See docs/seo-keyword-map.md. */
  seoTitle: string;
  /** 120–155 chars: primary keyword, one concrete fact, a call to action. */
  seoDescription: string;
  /** Deprecated: the meta keywords tag is no longer rendered. */
  seoKeywords?: string[];
  /** Answer-first intro shown directly under the H1 (40–60 words, facts register only). */
  tldr: string;
  /** Visible keyword phrase used as the Service name in JSON-LD (must appear on the page). */
  serviceName?: string;
  pricingFromGBP: number;
  /** Existing blog posts linked from the "Guides for [trade]" block. */
  guides?: { href: string; label: string }[];
  hero: {
    eyebrow: string;
    headline: string;
    highlight?: string;
    sub: string;
    primaryCta?: { label: string; href: string };
    secondaryCta?: { label: string; href: string };
  };
  slides?: Slide[];
  demoCategory?: string;
  demoSlugs?: string[];
  demosHeading?: string;
  demosSub?: string;
  value: {
    eyebrow: string;
    heading: string;
    intro?: string;
    points: { title: string; body: string; icon?: string }[];
  };
  features: {
    eyebrow: string;
    heading: string;
    intro?: string;
    items: { title: string; body: string }[];
  };
  /** Only real, owner-supplied testimonials. Leave empty until the owner provides them. */
  testimonials?: {
    quote: string;
    author: string;
    role: string;
    company: string;
  }[];
  faqs: { q: string; a: string }[];
  finalCta?: { headline: string; highlight?: string; sub?: string };
};

export { industryPages } from "@/lib/industry-pages";

// Only facts from the SEO facts register. Do not add client counts, lead
// increases or pipeline figures until the owner can verify them.
export const stats = [
  { value: "£1,000", label: "fixed price, one-off" },
  { value: "7", label: "days from call to launch" },
  { value: "18", label: "construction trades covered" },
  { value: "£0", label: "monthly fees" },
];

export const cities = [
  "London",
  "Manchester",
  "Leeds",
  "Bristol",
  "Birmingham",
  "Liverpool",
  "Sheffield",
  "Newcastle",
  "Brighton",
  "Doncaster",
  "Sussex",
  "Glasgow",
];
